import { defineStore } from "pinia";
import { computed, ref } from "vue";
import {
  getRequestPage as apiGetRequestPage,
  authToBase as apiAuthToBase,
  getBases as apiGetBases,
  getRequestTypes as apiGetRequestTypes,
  getRequestDetailsItem as apiGetRequestDetailsItem,
  getRequestHouses as apiGetRequestHouses,
  getRequestSourceTypes as apiGetRequestSourceTypes,
  getPremissesWithAccounts as apiGetPremissesWithAccounts,
  changeRequestAuthor as apiChangeRequestAuthor,
  setRequestWaiting as apiSetRequestWaiting,
  setSpecialComment as apiSetSpecialComment,
  changeRequestType as apiChangeRequestType,
  changeRequestSource as apiChangeRequestSource,
  changeRequestMalfunction as apiChangeRequestMalfunction,
  changeRequestPriority as apiChangeRequestPriority,
  changeRequestPhone as apiChangeRequestPhone,
  changeRequestCreationDate as apiChangeRequestCreationDate,
  changeConsultant as apiChangeConsultant,
  getHousesForUser as apiGetHousesForUser,
  getCompanyProfiles as apiGetCompanyProfiles,
  getUserCompanyProfiles as apiGetUserCompanyProfiles,
  getRequestTypesSupportService as apiGetRequestTypesSupportService,
  getMaterialTypes as apiGetMaterialTypes,
  getLastRequestSmartInfoDataByPremiseID as apiGetLastRequestSmartInfoDataByPremiseID,
  getDepartments as apiGetDepartments,
  getConsultants as apiGetConsultants,
  getAllConsultants as apiGetAllConsultants,
  getConsultantsToMoveRequest as apiGetConsultantsToMoveRequest,
  lockRequest as apiLockRequest,
  closeRequest as apiCloseRequest,
  closeRequestLowMark as apiCloseRequestLowMark,
  setCustomTemplate as apiSetCustomTemplate,
  getTemplateMessages as apiGetTemplateMessages,
  performRequest as apiPerformRequest,
  unperformRequest as apiUnperformRequest,
  unlockRequest as apiUnlockRequest,
  closeChildRequests as apiCloseChildRequests,
  getChildRequests as apiGetChildRequests,
  subscribeObserver as apiSubscribeObserver,
  addReminder as apiAddReminder,
  getTemplateFileNames as apiGetTemplateFileNames,
  downloadBlankForPrintFile as apiDownloadBlankForPrintFile,
  changeDateStartReport as apiChangeDateStartReport,
  changeDesiredTime as apiChangeDesiredTime,
  SetReportDate as apiSetReportDate,
  getEmployeesWhoSeeRequest as apiGetEmployeesWhoSeeRequest,
  getRequestPools as apiGetRequestPools,
  getHelpDispatcherGeneralInfoHouseID as apiGetHelpDispatcherGeneralInfoHouseID,
  getEmergencyWorksByHouse as apiGetEmergencyWorksByHouse,
  getLiftsForAccount as apiGetLiftsForAccount,
  getLiftsForHouse as apiGetLiftsForHouse,
  getGroupsOfDistrinctsForUser as apiGetGroupsOfDistrinctsForUser,
  getHouseGroupsForUser as apiGetHouseGroupsForUser,
  addHouseRequest as apiAddHouseRequest,
  addMessage as apiAddMessage,
  changeMessageVisibility as apiChangeMessageVisibility,
  downloadFile as apiDownloadFile,
  removeFile as apiRemoveFile,
  addFile as apiAddFile,
  performSearch as apiPerformSearch,
  performFullTextSearch as apiPerformFullTextSearch,
  debtorSearch as apiDebtorSearch,
  getMeters as apiGetMeters,
  getMetersValues as apiGetMetersValues,  
  getMetersByPeriod as apiGetMetersByPeriod,
  addMeterValueSimple as apiAddMeterValueSimple,
  addMeter as apiAddMeter,
  getMeterValuesHistory as apiGetMeterValuesHistory,
  getDebtorCasePage as apiGetDebtorCasePage,
  getUtilityShutdowns as apiGetUtilityShutdowns,
  addUtilityShutdown as apiAddUtilityShutdown,
  closeUtilityShutdown as apiCloseUtilityShutdown,
  deleteUtilityShutdown as apiDeleteUtilityShutdown,
} from "~/api/requestsDispatcher";
import {
  IRequestPageParams,
  IRequestPageResult,
  IAuthToBaseResult,
  IGetRequestTypesParams,
  IRequestTypesResult,
  IAuthToBaseResultError,
  IUserData,
  IRequestItem,
  IGetRequestHousesParams,
  IRequestHouseResult,
  IChangeRequestAuthorParams,
  ISetRequestWaiting,
  IChangeRequestTypeParams,
  IChangeRequestMalfunctionParams,
  IChangeRequestSourceParams,
  IRequestSourceTypesData,
  IChangeRequestPriorityParams,
  IGetPremissesWithAccountsParams,
  IPremiseData,
  IChangeRequestPhoneParams,
  IGetHousesForUserParams,
  IHousesForUserResult,
  IGetUserCompanyProfilesParams,
  IGetCompanyProfilesResult,
  IRequestTypesData,
  IGetMaterialTypesResult,
  IGetDepartmentsResult,
  IGetConsultantsParams,
  IGetConsultantsResult,
  IGetRequestPoolsResult,
  IAddHouseRequest,
  IChangeRequestCreationDateParams,
  IAddHouseRequestResult,
  IChangeConsultantParams,
  ISetSpecialCommentParams,
  IGetLastRequestSmartInfoDataByPremiseIDParams,
  IGetLastRequestSmartInfoDataByPremiseIDResult,
  IGetHelpDispatcherGeneralInfoHouseIDParams,
  IGetHelpDispatcherGeneralInfoHouseIDResult,
  IGetEmergencyWorksByHouseParams,
  IGetEmergencyWorksByHouseData,
  IGetLiftsForAccountParams,
  IGetLiftsResult,
  IGetLiftsForHouseParams,
  IGetGroupsOfDistrinctsForUserResult,
  IGetHouseGroupsForUserResult,
  IRequestSearchParams,
  IRequestSearchResult,
  IRequestFullTextSearchParams,
  ILockRequestParams,
  ICloseRequestParams,
  ISetCustomTemplateParams,
  ICustomTemplate,
  IPerformRequestParams,
  IUnperformRequestParams,
  IUnlockRequestParams,
  ICloseChildRequestsParams,
  IGetChildRequestsParams,
  IChildRequestListItem,
  ISubscribeObserverParams,
  IAddReminderParams,
  IDownloadBlankForPrintFileParams,
  IChangeDateStartReportParams,
  IChangeDesiredTimeParams,
  ISetReportDateParams,
  IEmployeesWhoSeeRequestParams,
  IEmployeesWhoSeeRequestItem,
  IUtilityShutdownListItem,
  IGetUtilityShutdownListParams,
  ICloseUtilityShutdownParams,
} from "~/types/requestsDispatcher";
import { getCurrentBase } from "~/helpers/persistenceBase";
import { ICommonResult, IDownloadFileResponse } from "~/types/common";
import { downloadBlobFile } from '@core/utils/downloadBlobFile';

export const useRequestsDispatcherStore = defineStore(
  "requestsDispatcher",
  () => {
    // State
    const basesList = ref<string[]>([]);
    const userData = ref<IUserData | null>(null);
    const authError = ref<unknown>(null);

    // Getters
    const currentBase = computed(() => getCurrentBase() || "");
    const acxToken = computed(() => (userData.value as IUserData)?.acx);

    // Request methods
    const authToBase = async (
      base: string
    ): Promise<IAuthToBaseResult | IAuthToBaseResultError> => {
      try {
        const response = await apiAuthToBase(base);
        return response;
      } catch (error) {
        throw error;
      }
    };

    const getBases = async () => {
      try {
        const response = await apiGetBases();

        if (!response) throw new Error("Ошибка получения списка баз");

        basesList.value = response.bases;
      } catch (error) {
        throw error;
      }
    };

    const performSearch = async (
      params: IRequestSearchParams
    ): Promise<IRequestSearchResult> => {
      try {
        return await apiPerformSearch(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const performFullTextSearch= async (
      params: IRequestFullTextSearchParams
    ): Promise<IRequestSearchResult> => {
      try {
        return await apiPerformFullTextSearch(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const debtorSearch = async (
      params: IRequestSearchParams
    ): Promise<IRequestSearchResult> => {
      try {
        return await apiDebtorSearch(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }


    const changeRequestCreationDate = async (
      params: IChangeRequestCreationDateParams
    ): Promise<ICommonResult> => {
      try {
        return await apiChangeRequestCreationDate(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const changeRequestType = async (
      params: IChangeRequestTypeParams
    ): Promise<ICommonResult> => {
      try {
        return await apiChangeRequestType(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const changeRequestAuthor = async (
      params: IChangeRequestAuthorParams
    ): Promise<ICommonResult> => {
      try {
        return await apiChangeRequestAuthor(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const changeRequestPriority = async (
      params: IChangeRequestPriorityParams
    ): Promise<ICommonResult> => {
      try {
        return await apiChangeRequestPriority(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const lockRequest = async (
      params: ILockRequestParams
    ): Promise<ICommonResult> => {
      try {
        return await apiLockRequest(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const closeRequest = async (
      params: ICloseRequestParams
    ): Promise<ICommonResult> => {
      try {
        return await apiCloseRequest(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const closeRequestLowMark = async (
      params: ICloseRequestParams
    ): Promise<ICommonResult> => {
      try {
        return await apiCloseRequestLowMark(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const setCustomTemplate = async (
      params: ISetCustomTemplateParams
    ): Promise<ICommonResult> => {
      try {
        return await apiSetCustomTemplate(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getTemplateMessages = async (): Promise<ICustomTemplate[]> => {
      try {
        return await apiGetTemplateMessages(acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const performRequest = async (
      params: IPerformRequestParams
    ): Promise<ICommonResult> => {
      try {
        return await apiPerformRequest(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const unperformRequest = async (
      params: IUnperformRequestParams
    ): Promise<ICommonResult> => {
      try {
        return await apiUnperformRequest(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const unlockRequest = async (
      params: IUnlockRequestParams
    ): Promise<ICommonResult> => {
      try {
        return await apiUnlockRequest(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }
 
    const closeChildRequests = async (
      params: ICloseChildRequestsParams
    ): Promise<ICommonResult> => {
      try {
        return await apiCloseChildRequests(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getChildRequests = async (
      params: IGetChildRequestsParams
    ): Promise<IChildRequestListItem[]> => {
      try {
        return await apiGetChildRequests(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const subscribeObserver = async (
      params: ISubscribeObserverParams
    ): Promise<ICommonResult> => {
      try {
        return await apiSubscribeObserver(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const addReminder = async (
      params: IAddReminderParams
    ): Promise<ICommonResult> => {
      try {
        return await apiAddReminder(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getTemplateFileNames = async (): Promise<string[]> => {
      try {
        return await apiGetTemplateFileNames(acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const downloadBlankForPrintFile = async (
      params: IDownloadBlankForPrintFileParams
    ): Promise<IDownloadFileResponse> => {
      try {
        return await apiDownloadBlankForPrintFile(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const changeDateStartReport = async (
      params: IChangeDateStartReportParams
    ): Promise<ICommonResult> => {
      try {
        return await apiChangeDateStartReport(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const changeDesiredTime = async (
      params: IChangeDesiredTimeParams
    ): Promise<ICommonResult> => {
      try {
        return await apiChangeDesiredTime(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const setReportDate = async (
      params: ISetReportDateParams
    ): Promise<ICommonResult> => {
      try {
        return await apiSetReportDate(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getEmployeesWhoSeeRequest = async (
      params: IEmployeesWhoSeeRequestParams
    ): Promise<IEmployeesWhoSeeRequestItem[]> => {
      try {
        return await apiGetEmployeesWhoSeeRequest(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const changeConsultant = async (
      params: IChangeConsultantParams
    ): Promise<ICommonResult> => {
      try {
        return await apiChangeConsultant(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const changeRequestMalfunction = async (
      params: IChangeRequestMalfunctionParams
    ): Promise<ICommonResult> => {
      try {
        return await apiChangeRequestMalfunction(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const changeRequestPhone = async (
      params: IChangeRequestPhoneParams
    ): Promise<ICommonResult> => {
      try {
        return await apiChangeRequestPhone(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const changeRequestSource = async (
      params: IChangeRequestSourceParams
    ): Promise<ICommonResult> => {
      try {
        return await apiChangeRequestSource(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }


    const setRequestWaiting = async (
      params: ISetRequestWaiting
    ): Promise<ICommonResult> => {
      try {
        return await apiSetRequestWaiting(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const setSpecialComment = async (
      params: ISetSpecialCommentParams
    ): Promise<ICommonResult> => {
      try {
        return await apiSetSpecialComment(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getRequestDetailsItem = async (
      id: number
    ): Promise<IRequestItem> => {
      try {
        return await apiGetRequestDetailsItem(id, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getRequestPage = async (
      params: IRequestPageParams
    ): Promise<IRequestPageResult> => {
      try {
        return await apiGetRequestPage(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    };

    const getRequestTypes = async (
      params?: IGetRequestTypesParams
    ): Promise<IRequestTypesResult> => {
      try {
        return await apiGetRequestTypes(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    };

    const getLiftsForAccount =async (
      params: IGetLiftsForAccountParams
    ): Promise<IGetLiftsResult[]> => {
      try {
        return await apiGetLiftsForAccount(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    };

    const getLiftsForHouse =async (
      params: IGetLiftsForHouseParams
    ): Promise<IGetLiftsResult[]> => {
      try {
        return await apiGetLiftsForHouse(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    };

    const getRequestTypesSupportService = async (
      params?: IGetRequestTypesParams
    ): Promise<IRequestTypesData[]> => {
      try {
        return await apiGetRequestTypesSupportService(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    };

    const getGroupsOfDistrinctsForUser = async (): Promise<IGetGroupsOfDistrinctsForUserResult[]> => {
      try {
        return await apiGetGroupsOfDistrinctsForUser(acxToken.value);
      } catch (error) {
        throw error;
      }
    };

    const getHouseGroupsForUser = async (): Promise<IGetHouseGroupsForUserResult[]> => {
      try {
        return await apiGetHouseGroupsForUser(acxToken.value);
      } catch (error) {
        throw error;
      }
    };

    const getEmergencyWorksByHouse = async (
      params: IGetEmergencyWorksByHouseParams
    ): Promise<IGetEmergencyWorksByHouseData[]> => {
      try {
        return await apiGetEmergencyWorksByHouse(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    };

    const getRequestSourceTypes = async (): Promise<IRequestSourceTypesData[]> => {
      try {
        return await apiGetRequestSourceTypes(acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getRequestHouses = async (
      params?: IGetRequestHousesParams
    ): Promise<IRequestHouseResult> => {
      try {
        return await apiGetRequestHouses(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getPremissesWithAccounts = async (
      params: IGetPremissesWithAccountsParams
    ): Promise<IPremiseData[]> => {
      try {
        return await apiGetPremissesWithAccounts(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getHousesForUser = async (
      params?: IGetHousesForUserParams
    ): Promise<IHousesForUserResult[]> => {
      try {
        return await apiGetHousesForUser(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getCompanyProfiles = async (): Promise<IGetCompanyProfilesResult[]> => {
      try {
        return await apiGetCompanyProfiles(acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getUserCompanyProfiles = async (
      params: IGetUserCompanyProfilesParams
    ): Promise<IGetCompanyProfilesResult[]> => {
      try {
        return await apiGetUserCompanyProfiles(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getMaterialTypes = async (
    ): Promise<IGetMaterialTypesResult[]> => {
      try {
        return await apiGetMaterialTypes(acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getDepartments = async (
    ): Promise<IGetDepartmentsResult[]> => {
      try {
        return await apiGetDepartments(acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getConsultants = async (
      params?: IGetConsultantsParams
    ): Promise<IGetConsultantsResult[]> => {
      try {
        return await apiGetConsultants(acxToken.value, params);
      } catch (error) {
        throw error;
      }
    }

    const getAllConsultants = async (): Promise<IGetConsultantsResult[]> => {
      try {
        return await apiGetAllConsultants(acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getConsultantsToMoveRequest = async (
      requestId: number
    ): Promise<IGetConsultantsResult[]> => {
      try {
        return await apiGetConsultantsToMoveRequest(acxToken.value, requestId);
      } catch (error) {
        throw error;
      }
    }

    const getRequestPools = async (
    ): Promise<IGetRequestPoolsResult[]> => {
      try {
        return await apiGetRequestPools(acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getLastRequestSmartInfoDataByPremiseID = async (
      params: IGetLastRequestSmartInfoDataByPremiseIDParams
    ): Promise<IGetLastRequestSmartInfoDataByPremiseIDResult> => {
      try {
        return await apiGetLastRequestSmartInfoDataByPremiseID(acxToken.value, params);
      } catch (error) {
        throw error;
      }
    }

    const getHelpDispatcherGeneralInfoHouseID = async (
      params: IGetHelpDispatcherGeneralInfoHouseIDParams
    ): Promise<IGetHelpDispatcherGeneralInfoHouseIDResult> => {
      try {
        return await apiGetHelpDispatcherGeneralInfoHouseID(acxToken.value, params);
      } catch (error) {
        throw error;
      }
    }

    const auth = async () => {
      try {
        const response = await authToBase(currentBase.value);

        // if (!response) throw new Error("Не удалось авторизоваться в системе");

        userData.value = {
          baseName: currentBase.value,
          ...(response as IAuthToBaseResult),
        };

        return response;
      } catch (error) {
        authError.value = error;
        throw error;
      }
    };

    const addHouseRequest = async (
      params: IAddHouseRequest
    ): Promise<IAddHouseRequestResult> => {
      try {
        return await apiAddHouseRequest(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const addFile = async (
      formData: FormData
    ): Promise<ICommonResult> => {
      try {
        return await apiAddFile(formData, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const addMessage = async (params: {id: number, text: string, IsHidden?: boolean}): Promise<ICommonResult> => {
      try {
        return await apiAddMessage(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const changeMessageVisibility = async (params: {requestId: number, messageId: number, isHidden: boolean}) => {
      try {
        return await apiChangeMessageVisibility(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }
    
    const downloadFile = async (id: number) => {
      try {
        const { data, headers } = await apiDownloadFile(id, acxToken.value);;
  
        downloadBlobFile(data, headers['content-disposition']);
      } catch (error) {
        throw error;
      }
    }

    const getImage = async (id: number) => {
      try {
        const { data, headers } = await apiDownloadFile(id, acxToken.value);;
        const URLApi = window.URL || window.webkitURL;
        const source = URLApi.createObjectURL(data);

        return source;
      } catch (error) {
        throw error;
      }
    }

    const removeFile = async (params: {requestId: number, fileId: number}) => {
      try {
        return await apiRemoveFile(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getMeters = async (ident: number) => {
      try {
        return await apiGetMeters(ident, acxToken.value);
      } catch (error) {
        throw error;
      }
    }
    const getMetersValues = async (id: number) => {
      try {
        return await apiGetMetersValues(id, acxToken.value);
      } catch (error) {
        throw error;
      }
    }
    const getMetersByPeriod = async (params: { ident: number, period: string }) => {
      try {
        return await apiGetMetersByPeriod(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const addMeterValueSimple = async (params: {  meterId: number, value: string, valueT2: string, valueT3: string }) => {
      try {
        return await apiAddMeterValueSimple(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }
    const addMeter = async (params: {  meterId: number, value: string, valueT2: string, valueT3: string }) => {
      try {
        return await apiAddMeter(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }
    const getMeterValuesHistory = async (id: number) => {
      try {
        return await apiGetMeterValuesHistory(id, acxToken.value);
      } catch (error) {
        throw error;
      }
    }
  
    const getDebtorCasePage = async (
      params: IRequestPageParams
    ): Promise<IRequestPageResult> => {
      try {
        return await apiGetDebtorCasePage(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const getUtilityShutdowns = async (
      params: IGetUtilityShutdownListParams
    ): Promise<IUtilityShutdownListItem[]> => {
      try {
        return await apiGetUtilityShutdowns(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const addUtilityShutdown = async (
      params: IGetUtilityShutdownListParams
    ): Promise<ICommonResult> => {
      try {
        return await apiAddUtilityShutdown(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const closeUtilityShutdown = async (
      params: ICloseUtilityShutdownParams
    ): Promise<ICommonResult> => {
      try {
        return await apiCloseUtilityShutdown(params, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    const deleteUtilityShutdown = async (
      id: number
    ): Promise<ICommonResult> => {
      try {
        return await apiDeleteUtilityShutdown(id, acxToken.value);
      } catch (error) {
        throw error;
      }
    }

    return {
      // State
      basesList,
      authError,
      userData,
      acxToken,
      // Methods
      getRequestPage,
      authToBase,
      getBases,
      getRequestTypes,
      getRequestTypesSupportService,
      getRequestDetailsItem,
      getRequestHouses,
      getRequestSourceTypes,
      getPremissesWithAccounts,
      getLiftsForAccount,
      getLiftsForHouse,
      getHousesForUser,
      getCompanyProfiles,
      getUserCompanyProfiles,
      getMaterialTypes,
      getDepartments,
      getGroupsOfDistrinctsForUser,
      getConsultants,
      getAllConsultants,
      getConsultantsToMoveRequest,
      getRequestPools,
      getEmergencyWorksByHouse,
      getLastRequestSmartInfoDataByPremiseID,
      getHelpDispatcherGeneralInfoHouseID,
      getHouseGroupsForUser,
      addHouseRequest,
      addFile,
      performSearch,
      performFullTextSearch,
      debtorSearch,
      changeRequestCreationDate,
      changeConsultant,
      changeRequestAuthor,
      changeRequestType,
      changeRequestMalfunction,
      changeRequestSource,
      changeRequestPriority,
      changeRequestPhone,
      setRequestWaiting,
      setSpecialComment,
      auth,
      lockRequest,
      closeRequest,
      closeRequestLowMark,
      setCustomTemplate,
      getTemplateMessages,
      performRequest,
      unperformRequest,
      unlockRequest,
      closeChildRequests,
      getChildRequests,
      subscribeObserver,
      addReminder,
      getTemplateFileNames,
      downloadBlankForPrintFile,
      changeDateStartReport,
      changeDesiredTime,
      setReportDate,
      getEmployeesWhoSeeRequest,
      addMessage,
      changeMessageVisibility,
      downloadFile,
      getImage,
      removeFile,
      getMeters,
      getMetersValues,
      getMetersByPeriod,
      addMeterValueSimple,
      addMeter,
      getMeterValuesHistory,
      getDebtorCasePage,
      getUtilityShutdowns,
      addUtilityShutdown,
      closeUtilityShutdown,
      deleteUtilityShutdown,
    };
  }
);
